(() => {
  const elementName = 'hf-delivery-estimator';
  const londonTimeZone = 'Europe/London';

  if (customElements.get(elementName)) return;

  class HeartfeltDeliveryEstimator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const dispatchElement = this.querySelector('[data-del-dispatch-value]');
      const deliveryElement = this.querySelector('[data-del-delivery-value]');
      const config = this.readConfig();

      if (!dispatchElement || !deliveryElement || !config) return;

      try {
        const calendarDate = this.getLondonCalendarDate(config.serverNow, config.timeZone);
        const earliestDispatch = this.addWorkingDays(calendarDate, config.dispatchMin);
        const latestDispatch = this.addWorkingDays(calendarDate, config.dispatchMax);
        const earliestDelivery = this.addWorkingDays(earliestDispatch, config.transitMin);
        const latestDelivery = this.addWorkingDays(latestDispatch, config.transitMax);
        const dispatchValue = this.formatRange(earliestDispatch, latestDispatch);
        const deliveryValue = this.formatRange(earliestDelivery, latestDelivery);

        dispatchElement.textContent = dispatchValue;
        deliveryElement.textContent = deliveryValue;
        this.dataset.enhanced = 'true';
      } catch (error) {
        return;
      }
    }

    readConfig() {
      const dispatchMin = this.readInteger(this.dataset.dispatchMin);
      const dispatchMax = this.readInteger(this.dataset.dispatchMax);
      const transitMin = this.readInteger(this.dataset.transitMin);
      const transitMax = this.readInteger(this.dataset.transitMax);

      if (
        dispatchMin === null ||
        dispatchMax === null ||
        transitMin === null ||
        transitMax === null ||
        !this.isSupportedDispatchRange(dispatchMin, dispatchMax) ||
        transitMin !== 2 ||
        transitMax !== 3
      ) {
        return null;
      }

      return {
        dispatchMin,
        dispatchMax,
        transitMin,
        transitMax,
        serverNow: this.readServerNow(this.dataset.serverNow),
        timeZone: this.readTimeZone(this.dataset.timeZone),
      };
    }

    isSupportedDispatchRange(minimum, maximum) {
      return (
        (minimum === 1 && maximum === 3) ||
        (minimum === 3 && maximum === 5) ||
        (minimum === 5 && maximum === 7) ||
        (minimum === 7 && maximum === 10)
      );
    }

    readInteger(value) {
      const normalized = String(value || '').trim();
      if (!/^\d+$/.test(normalized)) return null;

      const parsed = Number.parseInt(normalized, 10);
      return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
    }

    readServerNow(value) {
      const normalized = String(value || '').trim();
      const seconds = Number(normalized);
      const milliseconds = seconds * 1000;

      if (
        normalized !== '' &&
        Number.isFinite(seconds) &&
        Number.isFinite(new Date(milliseconds).getTime())
      ) {
        return milliseconds;
      }

      return Date.now();
    }

    readTimeZone(value) {
      return value === londonTimeZone ? value : londonTimeZone;
    }

    getLondonCalendarDate(instant, timeZone) {
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const parts = {};

      formatter.formatToParts(new Date(instant)).forEach((part) => {
        if (part.type !== 'literal') parts[part.type] = Number.parseInt(part.value, 10);
      });

      if (
        !Number.isInteger(parts.year) ||
        !Number.isInteger(parts.month) ||
        !Number.isInteger(parts.day)
      ) {
        throw new RangeError('Unable to derive the London calendar date');
      }

      return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    }

    addWorkingDays(date, count) {
      const result = new Date(date.getTime());
      let remaining = count;

      while (remaining > 0) {
        result.setUTCDate(result.getUTCDate() + 1);
        const day = result.getUTCDay();
        if (day !== 0 && day !== 6) remaining -= 1;
      }

      return result;
    }

    formatRange(start, end) {
      if (start.getTime() === end.getTime()) {
        return this.formatDate(start, false);
      }

      const crossesYear = start.getUTCFullYear() !== end.getUTCFullYear();
      return `${this.formatDate(start, crossesYear)} \u2013 ${this.formatDate(end, crossesYear)}`;
    }

    formatDate(date, includeYear) {
      return this.createDateFormatter(includeYear).format(date).replace(',', '');
    }

    createDateFormatter(includeYear) {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'UTC',
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        ...(includeYear ? { year: 'numeric' } : {}),
      });
    }
  }

  customElements.define(elementName, HeartfeltDeliveryEstimator);
})();
